import { RootState } from 'store/index';
import {
  CallScribePipelineStage,
  ICallScribeAccessInfo,
  ICallScribeCallListItem,
  ICallScribeCallTranscript,
  ICallScribeParticipant,
  ICallScribeVoiceToken,
  ILiveTranscriptSegment,
} from 'services/callScribe/types';
import { ICallScribeMyDashboard } from 'services/callScribe/dashboard';

export const CallScribeAccessSelector = (state: RootState): ICallScribeAccessInfo | undefined =>
  state.callScribe.access;

export const CallScribeAccessLoadingSelector = (state: RootState): boolean =>
  state.callScribe.accessLoading;

export const CallScribeVoiceTokenSelector = (state: RootState): ICallScribeVoiceToken | undefined =>
  state.callScribe.voiceToken;

export const CallScribeVoiceTokenLoadingSelector = (state: RootState): boolean =>
  state.callScribe.voiceTokenLoading;

export const CallScribeVoiceTokenErrorSelector = (state: RootState): string | undefined =>
  state.callScribe.voiceTokenError;

export const CallScribeCallsByTicketSelector = (state: RootState): ICallScribeCallListItem[] =>
  state.callScribe.callsByTicket;

export const CallScribeCallsLoadingSelector = (state: RootState): boolean =>
  state.callScribe.callsLoading;

export const CallScribeInitiatingCallSelector = (state: RootState): boolean =>
  state.callScribe.initiatingCall;

export const CallScribePostingTimeEntryForSelector = (state: RootState): number | undefined =>
  state.callScribe.postingTimeEntryFor;

/** Returns the cached transcript for a call: object = ready, null = checked-not-ready, undefined = never fetched. */
export const CallScribeTranscriptForCallSelector = (callId: number) =>
  (state: RootState): ICallScribeCallTranscript | null | undefined =>
    state.callScribe.transcriptsByCallId[callId];

export const CallScribeTranscriptLoadingForCallSelector = (callId: number) =>
  (state: RootState): boolean =>
    state.callScribe.transcriptLoadingFor[callId] ?? false;

export const CallScribePipelineForCallSelector = (callId: number | undefined) =>
  (state: RootState): { stage: CallScribePipelineStage; error: string | null } | undefined =>
    typeof callId === 'number' ? state.callScribe.pipelineByCallId[callId] : undefined;

export const CallScribeIntelligenceEventCountForCallSelector = (callId: number | undefined) =>
  (state: RootState): number =>
    typeof callId === 'number'
      ? state.callScribe.intelligenceEventCountByCallId[callId] ?? 0
      : 0;

export const CallScribeAccessErrorSelector = (state: RootState): string | undefined =>
  state.callScribe.accessError;

export const CallScribeCallsErrorSelector = (state: RootState): string | undefined =>
  state.callScribe.callsError;

export const CallScribeAnsweredAtForCallSelector = (callId: number | undefined) =>
  (state: RootState): string | undefined =>
    typeof callId === 'number' ? state.callScribe.answeredAtByCallId[callId] : undefined;

export const CallScribeTerminalStatusEventCountSelector = (state: RootState): number =>
  state.callScribe.terminalStatusEventCount;

export const CallScribeLiveTranscriptForCallSelector = (callId: number | undefined) =>
  (state: RootState): {
    committed: Record<string, ILiveTranscriptSegment>;
    partials: Record<string, ILiveTranscriptSegment | undefined>;
  } | undefined =>
    typeof callId === 'number' ? state.callScribe.liveTranscriptByCallId[callId] : undefined;

export const CallScribeActiveTranscriptCallSelector = (
  state: RootState,
): number | undefined => state.callScribe.activeTranscriptCallId;

export const CallScribeProtectedTranscriptCallSelector = (
  state: RootState,
): number | undefined => state.callScribe.protectedTranscriptCallId;

/** Per-tech dashboard projection (Sprint 7). Undefined until the first fetch resolves. */
export const CallScribeMyDashboardSelector = (state: RootState): ICallScribeMyDashboard | undefined =>
  state.callScribe.myDashboard;

export const CallScribeMyDashboardLoadingSelector = (state: RootState): boolean =>
  state.callScribe.myDashboardLoading;

export const CallScribeMyDashboardErrorSelector = (state: RootState): string | undefined =>
  state.callScribe.myDashboardError;

export const CallScribeParticipantsForCallSelector = (callId: number | undefined) =>
  (state: RootState): ICallScribeParticipant[] | undefined =>
    typeof callId === 'number' ? state.callScribe.participantsByCallId[callId] : undefined;
